import React, { useEffect, useState } from "react";
import "./AdminDashboard.css";
import { db, auth } from "../firebase";
import { collection, getDocs } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { getFunctions, httpsCallable } from "firebase/functions";

export default function AdminDashboard() {
  const [loading, setLoading] = useState(true);
  const [trailCount, setTrailCount] = useState(0);
  const [clueCount, setClueCount] = useState(0);
  const [userCount, setUserCount] = useState(0);
  const [userData, setUserData] = useState(null);
  const [unverifiedCount, setUnverifiedCount] = useState(0);
  const [supportCount, setSupportCount] = useState(0);
  const [userLoading, setUserLoading] = useState(true);

  const navigate = useNavigate();

  // Fetch user permissions
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        try {
          const usersRef = collection(db, "users");
          const usersSnap = await getDocs(usersRef);
          
          let userDoc = null;
          usersSnap.forEach((doc) => {
            if (doc.data().uid === user.uid || doc.id === user.uid) {
              userDoc = {
                id: doc.id,
                uid: user.uid,
                ...doc.data(),
              };
            }
          });

          if (userDoc) {
            setUserData(userDoc);
          } else {
            navigate("/dashboard");
          }
        } catch (error) {
          navigate("/dashboard");
        }
      } else {
        navigate("/login");
      }
      setUserLoading(false);
    });

    return () => unsubscribe();
  }, [navigate]);

  useEffect(() => {
    if (!userLoading && userData) {
      fetchStats();
    }
  }, [userLoading, userData]);

  async function fetchStats() {
    try {
      setLoading(true);
      
      const trailsSnap = await getDocs(collection(db, "trails"));
      setTrailCount(trailsSnap.size);

      const cluesSnap = await getDocs(collection(db, "clues"));
      setClueCount(cluesSnap.size);

      const usersSnap = await getDocs(collection(db, "users"));
      setUserCount(usersSnap.size);

      // Fetch support messages count
      try {
        const supportSnap = await getDocs(collection(db, "supportMessages"));
        const pendingSupport = supportSnap.docs.filter(doc => doc.data().status === "pending").length;
        setSupportCount(pendingSupport);
      } catch (err) {
        console.error("Error fetching support messages:", err);
        setSupportCount(0);
      }

      // Fetch unverified users from auth (separate try-catch so it doesn't break other stats)
      try {
        const functions = getFunctions();
        const getUnverifiedUsers = httpsCallable(functions, 'getUnverifiedUsers');
        const unverifiedResult = await getUnverifiedUsers();
        setUnverifiedCount(unverifiedResult.data.count);
      } catch (err) {
        console.error("Error fetching unverified count:", err);
        setUnverifiedCount(0);
      }
    } catch (err) {
      console.error("Error fetching stats:", err);
    } finally {
      setLoading(false);
    }
  }

  if (userLoading || loading) {
    return (
      <div className="dash-page">
        <div className="dash-loader">
          <div className="spinner"></div>
          <p>Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  const isAdmin = userData?.role === "admin";

  return (
    <div className="dash-page">
      <div className="dash-container">
        <header className="dash-header">
          <div className="dash-header-content">
            <h1 className="dash-title">Admin Dashboard</h1>
            <p className="dash-subtitle">
              Manage Trails, Clues, Users, Homepage & Advertisers
            </p>
          </div>
          {userData && (
            <div className="user-role-badge">
              <span className={`role-indicator ${userData.role}`}>
                {userData.role === "admin" ? "Admin" : "Editor"}
              </span>
            </div>
          )}
        </header>

        {/* Stats Grid */}
        <div className="admin-stats-grid">
          <div className="dash-card stat-card">
            <div className="stat-icon trails">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div className="stat-content">
              <h3>Total Trails</h3>
              <p className="stat-number">{trailCount}</p>
            </div>
          </div>

          <div className="dash-card stat-card">
            <div className="stat-icon clues">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2"/>
                <path d="M21 21L16.65 16.65" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </div>
            <div className="stat-content">
              <h3>Total Clues</h3>
              <p className="stat-number">{clueCount}</p>
            </div>
          </div>

          <div className="dash-card stat-card">
            <div className="stat-icon users">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                <path d="M12 8v4M12 16h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </div>
            <div className="stat-content">
              <h3>Unverified Users</h3>
              <p className="stat-number">{unverifiedCount}</p>
              {isAdmin && (
                <button
                  className="dash-btn primary small-btn"
                  onClick={() => navigate("/admin/unverified-users")}
                >
                  Manage Verification
                </button>
              )}
            </div>
          </div>

          <div className="dash-card stat-card">
            <div className="stat-icon users">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M17 21V19C17 17.9391 16.5786 16.9217 15.8284 16.1716C15.0783 15.4214 14.0609 15 13 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="2"/>
                <path d="M23 21V19C22.9993 18.1137 22.7044 17.2528 22.1614 16.5523C21.6184 15.8519 20.8581 15.3516 20 15.13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M16 3.13C16.8604 3.35031 17.623 3.85071 18.1676 4.55232C18.7122 5.25392 19.0078 6.11683 19.0078 7.005C19.0078 7.89318 18.7122 8.75608 18.1676 9.45769C17.623 10.1593 16.8604 10.6597 16 10.88" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            
            <div className="stat-content">
              <h3>Total Users</h3>
              <p className="stat-number">{userCount}</p>
              {isAdmin && (
                <button
                  className="dash-btn primary small-btn"
                  onClick={() => navigate("/admin/users")}
                >
                  Manage Users
                </button>
              )}
            </div>
          </div>

          <div className="dash-card stat-card">
            <div className="stat-icon support">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M13 11h.01M9 11h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </div>
            <div className="stat-content">
              <h3>Support Messages</h3>
              <p className="stat-number">{supportCount}</p>
              {isAdmin && (
                <button
                  className="dash-btn primary small-btn"
                  onClick={() => navigate("/admin/support-messages")}
                >
                  View Messages
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="dash-grid">
          <div className="dash-card">
            <div className="card-header">
              <h2>Trails Editor</h2>
              <p>Create and manage mystery trails</p>
            </div>
            <button
              className="dash-btn primary"
              onClick={() => navigate("/trailseditor")}
            >
              Open Trails Editor
            </button>
          </div>

          {isAdmin && (
            <>
              <div className="dash-card">
                <div className="card-header">
                  <h2>Homepage Management</h2>
                  <p>Edit hero content and banner</p>
                </div>
                <button
                  className="dash-btn primary"
                  onClick={() => navigate("/admin/homepage")}
                >
                  Edit Homepage
                </button>
              </div>

              <div className="dash-card">
                <div className="card-header">
                  <h2>Advertisers</h2>
                  <p>Manage advertiser sections and banners</p>
                </div>
                <button
                  className="dash-btn primary"
                  onClick={() => navigate("/admin/advertisers")}
                >
                  Advertiser Setup
                </button>
              </div>

              <div className="dash-card">
                <div className="card-header">
                  <h2>Wordsearch Generator</h2>
                  <p>Create auto-generated wordsearch images</p>
                </div>
                <button
                  className="dash-btn primary"
                  onClick={() => navigate("/admin/wordsearch")}
                >
                  Open Tool
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}